import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";

// components
import InvoiceDetailFilter from "./InvoiceDetailFilter";

// styles
import "../project/Project.css";
import useGetFetch from "../../hooks/useGetFetch";
import Loading from "../../components/Loading";
import { MatchingLoads } from "../../funcs/helperFuncs";
import InputField from "../../formComponents/InputField";
import { messageAndErrorStore, projectStore } from "../../store/Store";
import ErrorDisplay from "../../components/ErrorDisplay";
import AdjustmentDetails from "../../invoiceComponents/AdjustmentDetails";
import AddAdjustment from "../account-invoices/AddAdjustment";
import DeleteAdjustment from "../account-invoices/DeleteAdjustment";
import MessageCard from "../../components/MessageCard";
import DeliveredDate from "../../components/DeliveredDate";

export default function InvoiceDetails() {
  // global
  const currentProject = projectStore((data) => data.currentProject);
  const projectVendorInvoices = projectStore(
    (data) => data.projectVendorInvoices,
  );
  const projectLoads = projectStore((data) => data.projectLoads);
  const setProjectLoads = projectStore((data) => data.setProjectLoads);
  const projectAssetLoads = projectStore((data) => data.projectAssetLoads);
  const setProjectAssetLoads = projectStore(
    (data) => data.setProjectAssetLoads,
  );
  const messageTrigger = messageAndErrorStore((data) => data.messageTrigger);
  const pageError = messageAndErrorStore((messages) => messages.error);
  const successMessage = messageAndErrorStore(
    (messages) => messages.successMessage,
  );


  const [invoice, setInvoice] = useState(null);
  const [visibleLoads, setVisibleLoads] = useState([]);
  const [loads, setLoads] = useState([]);
  const [missing, setMissing] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [canceled, setCanceled] = useState([]);
  const [mismatchValue, setMismatchValue] = useState(2.0);
  const [loadsMap, setLoadsMap] = useState();
  const [assetMap, setAssetMap] = useState([]);
  const [mapPending, setMapPending] = useState(false);
  const [adjustments, setAdjustments] = useState();
  const [editAdjustmentTrigger, setEditAdjustmentTrigger] = useState(false);
  const [deleteAdjustmentTrigger, setDeleteAdjustmentTrigger] = useState(false);
  const { invId } = useParams("invId");
  const { type } = useParams("type");

  
  const { pending, error } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/vendor/invoice/${invId}`,
    setInvoice,
  );
  const { pending: loadsPending, error: loadsError } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/loads/${currentProject.project.id}`,
    setProjectLoads,
  );
  const { pending: assetsPending, error: assetsError } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/asset-loads/${currentProject.project.id}`,
    setProjectAssetLoads,
  );

  const { isPending: adjustmentsPending, error: adjustmentsError } =
    useGetFetch(
      `${process.env.REACT_APP_BACKEND}/lc/invoice-adjustment/${invId}?is_vendor=true&is_received=false&project_id=${currentProject.project.id}`,
      setAdjustments,
    );

  // map loads
  useEffect(() => {
    setMapPending(true);
    if (projectLoads && invoice && invoice.loads) {
      let map = {};
      projectLoads.forEach((l) => {
        var vLd = invoice.loads.filter((x) => x.load_id === l.id);
        if (vLd.length !== 0) {
          map[l.id] = {
            calc_weight: 0,
            weight_diff: 0,
            calc_cost: 0,
            cost_diff: 0,
            cost_mismatch: "",
            missing_ticket: l.ticket_is_missing,
            ticket_number: l.ticket_number,
            locked_reason: l.locked_reason,
            matching_load_numbers: l.matching_load_numbers,
          };
          map[l.id].calc_weight = l.weight_tons * (1 - vLd[0].moisture_content);
          map[l.id].weight_diff = vLd[0].weight_tons - map[l.id].calc_weight;
          map[l.id].calc_cost =
            type === "freight"
              ? l.freight_cost + l.fsc_cost
              : type === "sand" && l.sand_cost * (1 - vLd[0].moisture_content);
          map[l.id].cost_diff = vLd[0].total_cost - map[l.id].calc_cost;
          map[l.id].cost_mismatch =
            map[l.id].cost_diff > mismatchValue ||
            map[l.id].cost_diff < -mismatchValue
              ? "Yes"
              : "";
        }
      });
      setLoadsMap(map);
    }
    if (projectAssetLoads && invoice && invoice.loads) {
      let map = {};
      projectAssetLoads.forEach((l) => {
        var vLd = invoice.loads.filter((x) => x.load_id === l.id);
        if (vLd.length !== 0) {
          map[l.id] = {
            calc_weight: 0,
            weight_diff: 0,
            calc_cost: 0,
            cost_diff: 0,
            cost_mismatch: "",
            locked_reason: l.locked_reason,
          };
          map[l.id].calc_weight = l.weight_tons * (1 - vLd[0].moisture_content);
          map[l.id].weight_diff = vLd[0].weight_tons - map[l.id].calc_weight;
          map[l.id].calc_cost = type === "asset" && l.freight_cost + l.fsc_cost;
          map[l.id].cost_diff = vLd[0].total_cost - map[l.id].calc_cost;
          map[l.id].cost_mismatch =
            map[l.id].cost_diff > mismatchValue ||
            map[l.id].cost_diff < -mismatchValue
              ? "Yes"
              : "";
        }
      });
      setAssetMap(map);
    }
    setMapPending(false);
  }, [projectLoads, invoice, type, projectAssetLoads, mismatchValue]);

  useEffect(() => {
    if (invoice && invoice.loads) {
      const duplicates = [];
      const missing = [];
      const regular = [];
      const canceled = [];
      invoice.loads.forEach((l) => {
        if (l.duplicate_of !== 0) {
          duplicates.push(l);
        } else if (l.load_is_missing) {
          missing.push(l);
        } else if (l.is_canceled) {
          canceled.push(l);
        } else {
          regular.push(l);
        }
      });
      setDuplicates(duplicates);
      setMissing(missing);
      setCanceled(canceled);
      setLoads(regular);
      setVisibleLoads(regular);
    }
  }, [invoice, setDuplicates, setMissing, setLoads]);

  const getDuplicateName = (invoiceId) => {
    if (invoiceId !== 0) {
      const duplicate = projectVendorInvoices.invoices.filter(
        (x) => x.id === invoiceId,
      )[0];
      if (duplicate) {
        return duplicate.invoice_name;
      } else {
        return "";
      }
    } else {
      return "";
    }
  };

  const getMappedValue = (lId, val) => {
    var blank = {
      calc_weight: 0,
      weight_diff: 0,
      calc_cost: 0,
      cost_diff: 0,
      cost_mismatch: "",
      missing_ticket: false,
      ticket_number: "",
      locked_reason: "",
      matching_load_numbers: [0],
    };
    if (type !== "asset" && loadsMap[lId]) {
      return loadsMap[lId][val];
    } else if (assetMap[lId]) {
      return assetMap[lId][val];
    } else {
      return blank;
    }
  };

  const getUninvoiceLoadValues = (id) => {
    var l = {
      missing_ticket: "",
      matching_load_numbers: [],
      locked_reason: "",
    };
    if (type === "asset") {
      var load = projectAssetLoads.filter((x) => x.id === id);
      if (load.length > 0) {
        l.locked_reason = load[0].locked_reason;
      }
    } else {
      load = projectLoads.filter((x) => x.id === id);
      if (load.length > 0) {
        l.locked_reason = load[0].locked_reason;
        l.matching_load_numbers = load[0].matching_load_numbers;
        if (load[0].ticket_is_missing) {
          l.missing_ticket = "Yes";
        }
      }
    }
    return l;
  };

  if (
    pending ||
    loadsPending ||
    assetsPending ||
    mapPending ||
    adjustmentsPending
  ) {
    return <Loading />;
  } else if (error || loadsError || assetsError || adjustmentsError) {
    return <ErrorDisplay message={"Something Went Wrong"} code={500} />;
  } else if (invoice && loadsMap) {
    return (
      <div className="container-fluid">
        {messageTrigger && (
          <MessageCard
            errorMessage={pageError}
            successMessage={successMessage}
          />
        )}
        {error && <p className="error">{error}</p>}
        <div className="d-flex justify-content-between project-header">
          <h2>Invoice : {invoice.invoice.invoice_name}</h2>
          <InputField
            name={"mismatch-value"}
            title={"Mismatch Threshold $"}
            value={mismatchValue}
            type={"number"}
            increment={0.01}
            onChange={(e) => setMismatchValue(e.target.value)}
            required
          />
        </div>
        {adjustments &&
          adjustments.invoice_adjustment &&
          adjustments.adjustment_fields && (
            <AdjustmentDetails
              adjustments={adjustments}
              setEditAdjustmentTrigger={setEditAdjustmentTrigger}
              setDeleteAdjustmentTrigger={setDeleteAdjustmentTrigger}
            />
          )}
        {canceled.length !== 0 && (
          <>
            <div className="d-flex justify-content-between project-header">
              <h4>Canceled Loads</h4>
            </div>
            <div className="load-display-list">
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Load</th>
                    <th>Delivered</th>
                    <th>Driver</th>
                    {type !== "asset" && <th>Ticket</th>}
                    <th>Weight</th>
                    {type !== "asset" && <th>Product</th>}
                    <th>Calc Weight</th>
                    <th>Weight Diff</th>
                    {type !== "sand" ? <th>Freight</th> : <th>Sand</th>}
                    {type !== "sand" && <th>FSC</th>}
                    <th>Total</th>
                    <th>Calculated Total</th>
                    <th>Difference</th>
                    {type !== "asset" && <th>Missing Ticket</th>}
                    <th>Locked Reason</th>
                    {type !== "asset" && <th>Duplicate Tickets</th>}
                  </tr>
                </thead>
                <tbody>
                  {canceled.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.load_no}</td>
                      <DeliveredDate d={l.delivered_date} />
                      <td>{l.driver_name}</td>
                      <td>{l.load_no}</td>
                      {type !== "asset" && <td>{l.ticket_number}</td>}
                      <td>{l.weight_tons.toFixed(2)}</td>
                      {type !== "asset" && <td>{l.product_name}</td>}
                      <td>
                        {getMappedValue(l.load_id, "calc_weight").toFixed(2)}
                      </td>
                      <td>
                        {getMappedValue(l.load_id, "weight_diff").toFixed(2)}
                      </td>
                      {type !== "sand" ? (
                        <td>${l.freight_cost.toFixed(2)}</td>
                      ) : (
                        <td>${l.sand_cost.toFixed(2)}</td>
                      )}
                      {type !== "sand" && <td>${l.vendor_fsc.toFixed(2)}</td>}
                      <td>${l.total_cost.toFixed(2)}</td>
                      <td>
                        ${getMappedValue(l.load_id, "calc_cost").toFixed(2)}
                      </td>
                      <td>
                        ${getMappedValue(l.load_id, "cost_diff").toFixed(2)}
                      </td>
                      {type !== "asset" && (
                        <td>{getMappedValue(l.load_id, "missing_ticket")}</td>
                      )}
                      <td>{getMappedValue(l.load_id, "locked_reason")}</td>
                      {type !== "asset" && (
                        <MatchingLoads
                          loadNumbers={getMappedValue(
                            l.load_id,
                            "matching_load_numbers",
                          )}
                        />
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {duplicates.length !== 0 && (
          <>
            <div className="d-flex justify-content-between project-header">
              <h4>Duplicate Loads</h4>
            </div>
            <div className="load-display-list">
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Load</th>
                    <th>Carrier</th>
                    <th>Driver</th>
                    <th>Duplicate From</th>
                    {type !== "asset" && <th>Ticket</th>}
                    <th>Weight</th>
                    {type !== "asset" && <th>Product</th>}
                    <th>Calc Weight</th>
                    <th>Weight Diff</th>
                    {type !== "sand" ? <th>Freight</th> : <th>Sand</th>}
                    {type !== "sand" && <th>FSC</th>}
                    <th>Total</th>
                    <th>Calculated Total</th>
                    <th>Difference</th>
                    {type !== "asset" && <th>Missing Ticket</th>}
                    <th>Locked Reason</th>
                    {type !== "asset" && <th>Duplicate Tickets</th>}
                  </tr>
                </thead>
                <tbody>
                  {duplicates.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.load_no}</td>
                      <td>{l.carrier_name}</td>
                      <td>{l.driver_name}</td>
                      <td>{getDuplicateName(l.duplicate_of)}</td>
                      {type !== "asset" && <td>{l.ticket_number}</td>}
                      <td>{l.weight_tons.toFixed(2)}</td>
                      {type !== "asset" && <td>{l.product_name}</td>}
                      <td>
                        {getMappedValue(l.load_id, "calc_weight").toFixed(2)}
                      </td>
                      <td>
                        {getMappedValue(l.load_id, "weight_diff").toFixed(2)}
                      </td>
                      {type !== "sand" ? (
                        <td>${l.freight_cost.toFixed(2)}</td>
                      ) : (
                        <td>${l.sand_cost.toFixed(2)}</td>
                      )}
                      {type !== "sand" && <td>${l.fsc_cost.toFixed(2)}</td>}
                      <td>${l.total_cost.toFixed(2)}</td>
                      <td>
                        ${getMappedValue(l.load_id, "calc_cost").toFixed(2)}
                      </td>
                      <td>
                        ${getMappedValue(l.load_id, "cost_diff").toFixed(2)}
                      </td>
                      {type !== "asset" && (
                        <td>{getMappedValue(l.load_id, "missing_ticket")}</td>
                      )}
                      <td>{getMappedValue(l.load_id, "locked_reason")}</td>
                      {type !== "asset" && (
                        <MatchingLoads
                          loadNumbers={getMappedValue(
                            l.load_id,
                            "matching_load_numbers",
                          )}
                        />
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {missing.length !== 0 && (
          <>
            <div className="d-flex justify-content-between project-header">
              <h4>Missing Loads</h4>
            </div>
            <div className="load-display-list">
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    {type !== "sand" ? <th>Load</th> : <th>Ticket</th>}
                    <th>Weight</th>
                    {type !== "sand" ? <th>Freight</th> : <th>Sand</th>}
                    {type !== "sand" && <th>FSC</th>}
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {missing.map((l, idx) => (
                    <tr key={idx}>
                      {type !== "sand" ? (
                        <td>{l.load_no}</td>
                      ) : (
                        <td>{l.ticket_number}</td>
                      )}
                      <td>{l.weight_tons}</td>
                      {type !== "sand" ? (
                        <td>${l.freight_cost.toFixed(2)}</td>
                      ) : (
                        <td>${l.sand_cost.toFixed(2)}</td>
                      )}
                      {type !== "sand" && <td>${l.vendor_fsc.toFixed(2)}</td>}
                      <td>${l.total_cost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {invoice.uninvoiced_loads && (
          <div className="load-display-list">
            <h4>Loads Not Invoiced</h4>
            <table className="table table-sm table-hover">
              <thead>
                <tr>
                  <th>Load</th>
                  <th>Delivered</th>
                  <th>Carrier</th>
                  <th>Driver</th>
                  {type !== "asset" && <th>Ticket</th>}
                  <th>Weight</th>
                  {type !== "asset" && <th>Product</th>}
                  {type !== "sand" ? <th>Freight</th> : <th>Sand</th>}
                  {type !== "sand" && <th>FSC</th>}
                  <th>Total</th>
                  {type !== "asset" && <th>Missing Ticket</th>}
                  <th>Locked Reason</th>
                  {type !== "asset" && <th>Duplicate Tickets</th>}
                  <th>Approval Status</th>
                </tr>
              </thead>
              <tbody>
                {invoice.uninvoiced_loads.map((ld, i) => (
                  <tr key={i}>
                    <td>{ld.load_no}</td>
                    <DeliveredDate d={ld.delivered_date} />
                    <td>{ld.carrier_name}</td>
                    <td>{ld.driver_name}</td>
                    {type !== "asset" && <td>{ld.ticket_number}</td>}
                    <td>{ld.weight_tons.toFixed(2)}</td>
                    {type !== "asset" && <td>{ld.product_name}</td>}
                    {type !== "sand" ? (
                      <td>${ld.freight_cost.toFixed(2)}</td>
                    ) : (
                      <td>${ld.sand_cost.toFixed(2)}</td>
                    )}
                    {type !== "sand" && <td>${ld.fsc_cost.toFixed(2)}</td>}
                    <td>${ld.total_cost.toFixed(2)}</td>
                    {type !== "asset" && (
                      <td>
                        {getUninvoiceLoadValues(ld.load_id).missing_ticket}
                      </td>
                    )}
                    <td>{getUninvoiceLoadValues(ld.load_id).locked_reason}</td>
                    {type !== "asset" && (
                      <MatchingLoads
                        loadNumbers={
                          getUninvoiceLoadValues(ld.load_id)
                            .matching_load_numbers
                        }
                      />
                    )}
                    <td>{ld.propconnect_approval_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="d-flex justify-content-between project-header">
          {loads.length !== 0 && <h4>Invoiced Loads</h4>}
        </div>
        {loads.length !== 0 && type !== "asset" ? (
          <div className="row g-2">
            <InvoiceDetailFilter
              loads={loads}
              setVisibleLoads={setVisibleLoads}
              type={type}
              loadsMap={loadsMap}
            />
          </div>
        ) : (
          loads.length !== 0 &&
          type === "asset" && (
            <div className="row g-2">
              <InvoiceDetailFilter
                loads={loads}
                setVisibleLoads={setVisibleLoads}
                type={type}
                loadsMap={assetMap}
              />
            </div>
          )
        )}
        <div className="load-display-list">
          {loads.length !== 0 && (
            <table className="table table-sm table-hover">
              <thead>
                <tr>
                  <th>Load</th>
                  <th>Delivered</th>
                  <th>Carrier</th>
                  <th>Driver</th>
                  {type !== "asset" && <th>Ticket</th>}
                  <th>Weight</th>
                  {type !== "asset" && <th>Product</th>}
                  <th>Calc Weight</th>
                  <th>Weight Diff</th>
                  {type !== "sand" ? <th>Freight</th> : <th>Sand</th>}
                  {type !== "sand" && <th>FSC</th>}
                  <th>Total</th>
                  <th>Calculated Total</th>
                  <th>Difference</th>
                  <th>Cost Mismatch</th>
                  {type !== "asset" && <th>Missing Ticket</th>}
                  <th>Locked Reason</th>
                  {type !== "asset" && <th>Duplicate Tickets</th>}
                </tr>
              </thead>
              <tbody>
                {visibleLoads.length !== 0 &&
                  visibleLoads.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.load_no}</td>
                      <DeliveredDate d={l.delivered_date} />
                      <td>{l.carrier_name}</td>
                      <td>{l.driver_name}</td>
                      {type !== "asset" && (
                        <td>{getMappedValue(l.load_id, "ticket_number")}</td>
                      )}
                      <td>{l.weight_tons.toFixed(2)}</td>
                      {type !== "asset" && <td>{l.product_name}</td>}
                      <td>
                        {getMappedValue(l.load_id, "calc_weight").toFixed(2)}
                      </td>
                      <td>
                        {getMappedValue(l.load_id, "weight_diff").toFixed(2)}
                      </td>
                      {type !== "sand" ? (
                        <td>${l.freight_cost.toFixed(2)}</td>
                      ) : (
                        <td>${l.sand_cost.toFixed(2)}</td>
                      )}
                      {type !== "sand" && <td>${l.fsc_cost.toFixed(2)}</td>}
                      <td>${l.total_cost.toFixed(2)}</td>
                      <td>
                        ${getMappedValue(l.load_id, "calc_cost").toFixed(2)}
                      </td>
                      <td>
                        ${getMappedValue(l.load_id, "cost_diff").toFixed(2)}
                      </td>
                      <td>{getMappedValue(l.load_id, "cost_mismatch")}</td>
                      {type !== "asset" && (
                        <td>
                          {getMappedValue(l.load_id, "missing_ticket")
                            ? "Yes"
                            : "No"}
                        </td>
                      )}
                      <td>{getMappedValue(l.load_id, "locked_reason")}</td>
                      {type !== "asset" && (
                        <MatchingLoads
                          loadNumbers={getMappedValue(
                            l.load_id,
                            "matching_load_numbers",
                          )}
                        />
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
        {editAdjustmentTrigger && adjustments && (
          <AddAdjustment
            inv={invoice.invoice}
            setTrigger={setEditAdjustmentTrigger}
            projectId={currentProject.project.id}
            isVendor={true}
            id={adjustments.invoice_adjustment.id}
            editAdjustment={adjustments.invoice_adjustment}
            editAdjustmentFields={adjustments.adjustment_fields}
            setAdjustments={setAdjustments}
          />
        )}
        {deleteAdjustmentTrigger && (
          <DeleteAdjustment
            setTrigger={setDeleteAdjustmentTrigger}
            id={adjustments.invoice_adjustment.id}
            setAdjustments={setAdjustments}
            name={invoice.invoice.invoice_name}
          />
        )}
      </div>
    );
  }
}
